import {jobStatusLabel} from './job-status-badge'
import type {JobDetail, JobEditFormValues, JobLifecycleStatus, JobLocation} from './types'
import {createEmptyLocation} from './types'

export function locationFromDetail(location: JobDetail['locations'][number]): JobLocation {
    return {
        name: location.name,
        notes: location.notes ?? '',
        positionMode: 'latlng',
        latitude: location.latitude,
        longitude: location.longitude,
        addressText: null,
    }
}

export function jobDetailToEditFormValues(job: JobDetail): JobEditFormValues {
    return {
        jobName: job.name,
        serviceTypeId: job.serviceTypeId,
        notes: job.notes ?? '',
        customerId: job.customerId,
        locations:
            job.locations.length > 0
                ? job.locations.map(locationFromDetail)
                : [createEmptyLocation()],
        requirements: job.requirements.map((requirement) => ({
            requirementType: requirement.requirementType,
            quantity: requirement.quantity,
            workerTypeId: requirement.workerTypeId ?? null,
            rentalAssetTypeId: requirement.rentalAssetTypeId,
        })),
        assignments: job.assignments.map((assignment) => ({
            workerId: assignment.workerId,
            rentalAssetId: assignment.rentalAssetId,
        })),
    }
}

export type JobLifecycleAction = 'approve' | 'archive' | 'cancel' | 'reopen'

export type JobStatusModalAction = {
    label: string
    lifecycleAction: JobLifecycleAction
    variant?: 'filled' | 'default'
    destructive?: boolean
}

export type JobStatusModalContent = {
    title: string
    currentDescription: string
    nextDescription: string | null
    actions: JobStatusModalAction[]
}

export function getJobStatusModalContent(status: JobLifecycleStatus): JobStatusModalContent {
    switch (status) {
        case 'estimate':
            return {
                title: jobStatusLabel(status),
                currentDescription:
                    'This job is still being planned. It has not been released to dispatch yet.',
                nextDescription:
                    'Choosing "Ready for dispatch" moves the job to the active pipeline for driver and equipment assignment. This will allow drivers to view the job and accept it. Choosing "Cancel job" indicates the dispatch task will not proceed.',
                actions: [
                    {label: 'Ready for dispatch', lifecycleAction: 'approve'},
                    {label: 'Cancel job', lifecycleAction: 'cancel', destructive: true},
                ],
            }
        case 'in_progress':
            return {
                title: jobStatusLabel(status),
                currentDescription:
                    'This job is active and available to be assigned to workers and equipment. Drivers can view the job and accept it.',
                nextDescription:
                    'Choosing "Close job" will complete the job and hide it from the default jobs list. Invoicing is handled at the order level.',
                actions: [{label: 'Close job', lifecycleAction: 'archive', destructive: true}],
            }
        case 'closed':
            return {
                title: jobStatusLabel(status),
                currentDescription: 'This job is complete and hidden from the default jobs list.',
                nextDescription:
                    'Choosing "Reopen" will return the job to Active so it reappears in the default list and can be shared with drivers again.',
                actions: [{label: 'Reopen', lifecycleAction: 'reopen'}],
            }
        case 'canceled':
            return {
                title: jobStatusLabel(status),
                currentDescription:
                    'This job has been canceled and is hidden from the default jobs list.',
                nextDescription:
                    'Choosing "Reopen estimate" will return the job to Estimate so it can be planned and released to dispatch again.',
                actions: [{label: 'Reopen estimate', lifecycleAction: 'reopen'}],
            }
    }
}

export function didNotHappenNextStep(status: JobLifecycleStatus): string {
    return status === 'estimate'
        ? 'Cancel the job or move its date.'
        : 'Move its date or close the job.'
}

export function formatRequirementLabel(requirement: JobDetail['requirements'][number]) {
    if (requirement.requirementType === 'worker') {
        return `${requirement.quantity} worker${requirement.quantity === 1 ? '' : 's'}`
    }

    const typeName = requirement.rentalAssetTypeName ?? 'rental asset'
    return `${requirement.quantity} ${typeName}${requirement.quantity === 1 ? '' : 's'}`
}
